// ThemeContext.js

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

// Styled Components
import { createGlobalStyle } from 'styled-components';

import { useBase } from 'context/BaseContext';
import { useUser } from 'context/UserContext';
import { createSetting, updateSetting } from 'services/settings/SettingWriteService';
import {
  resolveBaseThemePack,
  subscribeToBaseTheme,
} from 'services/theme/BaseThemeService';
import { updateBase } from 'services/bases';
import {
  applyThemeCssVariables,
  buildThemeCssVariables,
} from './themeVariables';
import {
  ORBITAL_DEFAULT_PACK,
  ThemeMode,
  buildThemeMapForPack,
  normalizeThemeMode,
  resolveTheme,
} from './theme';

export const useStyle = createGlobalStyle;

export { ThemeMode };

const STORAGE_KEY = 'base-theme-mode.v1';
const SETTINGS_THEME_OVERRIDE_KEY = 'THEME_MODE_OVERRIDE_BY_BASE';
const LEGACY_SETTINGS_THEME_OVERRIDE_KEY = 'THEME_MODE_OVERRIDE_BY_WORKSPACE';
const BASE_DEFAULT_THEME_FIELD = 'themeModeDefault';

/** Builtin pack modes resolved once for context defaults / `themes` map. */
const THEME_MAP = buildThemeMapForPack(ORBITAL_DEFAULT_PACK);

const DEFAULT_THEME = THEME_MAP[ThemeMode.DARK];

export const ThemeContext = createContext({
  themeMode: ThemeMode.DARK,
  theme: DEFAULT_THEME,
  themePackId: ORBITAL_DEFAULT_PACK.id,
  setThemeMode: () => {},
  setThemeModeOverride: () => {},
  clearThemeModeOverride: () => {},
  setBaseDefaultThemeMode: () => {},
  isUsingBaseThemeDefault: true,
  baseDefaultThemeMode: ThemeMode.DARK,
  themePreferenceSource: 'fallback',
  themes: THEME_MAP,
});

const readStoredPreferences = () => {
  try {
    if (typeof window === 'undefined') return {};
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed ? parsed : {};
  } catch (err) {
    console.warn('ThemeContext: failed to read base theme preferences', err);
    return {};
  }
};

function getBaseThemeOverrideMap(userSettings) {
  const next = userSettings?.[SETTINGS_THEME_OVERRIDE_KEY];
  const legacy = userSettings?.[LEGACY_SETTINGS_THEME_OVERRIDE_KEY];
  const candidate = (next && typeof next === 'object' && !Array.isArray(next))
    ? next
    : ((legacy && typeof legacy === 'object' && !Array.isArray(legacy)) ? legacy : null);
  if (!candidate) {
    return {};
  }

  return candidate;
}

function buildThemeOverrideSettingPatch(overrideMap) {
  return {
    [SETTINGS_THEME_OVERRIDE_KEY]: overrideMap,
    [LEGACY_SETTINGS_THEME_OVERRIDE_KEY]: overrideMap,
  };
}

/**
 * ThemeOverrideProvider
 *
 * Applies a nested theme mode override while preserving the parent theme actions.
 *
 * @param {ReactNode} children - Nested content that should inherit the override.
 * @param {string|null} themeMode - Optional theme mode override to apply.
 * @param {boolean} [useDefaultPack=false] - When true, resolve colors from the
 *   builtin default pack only (ignore the active base's custom theme).
 *
 * @returns {ReactNode} The nested provider when an override is present.
 */
export const ThemeOverrideProvider = ({
  children,
  themeMode = null,
  useDefaultPack = false,
}) => {
  const parentValue = useContext(ThemeContext);

  const overriddenValue = useMemo(() => {
    const normalizedThemeMode = normalizeThemeMode(themeMode);

    if (!normalizedThemeMode) {
      return parentValue;
    }

    const themes = useDefaultPack
      ? THEME_MAP
      : (parentValue.themes || THEME_MAP);

    return {
      ...parentValue,
      isUsingBaseThemeDefault: true,
      theme: themes[normalizedThemeMode]
        || resolveTheme(ORBITAL_DEFAULT_PACK, normalizedThemeMode),
      themeMode: normalizedThemeMode,
      themePackId: useDefaultPack
        ? ORBITAL_DEFAULT_PACK.id
        : (parentValue.themePackId || ORBITAL_DEFAULT_PACK.id),
      themePreferenceSource: 'public-override',
      baseDefaultThemeMode: normalizedThemeMode,
      themes,
    };
  }, [parentValue, themeMode, useDefaultPack]);

  const normalizedThemeMode = normalizeThemeMode(themeMode);

  useEffect(() => {
    if (!normalizedThemeMode) {
      return undefined;
    }

    const rootStyle = document.documentElement?.style;
    if (!rootStyle) {
      return undefined;
    }

    const overrideVariables = buildThemeCssVariables(overriddenValue.theme);
    applyThemeCssVariables(rootStyle, overrideVariables);

    return () => {
      applyThemeCssVariables(
        rootStyle,
        buildThemeCssVariables(parentValue.theme),
      );
    };
  }, [normalizedThemeMode, overriddenValue.theme, parentValue.theme]);

  if (!normalizedThemeMode) {
    return children;
  }

  return (
    <ThemeContext.Provider value={overriddenValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const ThemeProvider = ({ children }) => {
  const { activeBase, setActiveBase } = useBase();
  const { user, userSettings, setUserSettings } = useUser();
  const migratedLegacyBaseRef = useRef(new Set());
  const [baseThemeDoc, setBaseThemeDoc] = useState(null);

  const activeBaseKey = typeof activeBase?.key === 'string'
    ? activeBase.key.trim()
    : '';

  const userKey = typeof user?.key === 'string'
    ? user.key.trim()
    : '';

  useEffect(() => {
    if (!activeBaseKey || !userKey) {
      setBaseThemeDoc(null);
      return undefined;
    }

    return subscribeToBaseTheme(
      activeBaseKey,
      (nextTheme) => {
        setBaseThemeDoc(nextTheme || null);
      },
      (error) => {
        console.error('ThemeContext: failed to subscribe to base theme', error);
        setBaseThemeDoc(null);
      },
    );
  }, [activeBaseKey, userKey]);

  const activeThemePack = useMemo(
    () => resolveBaseThemePack(baseThemeDoc),
    [baseThemeDoc],
  );

  const themes = useMemo(
    () => buildThemeMapForPack(activeThemePack),
    [activeThemePack],
  );

  const themeModeByBaseOverride = useMemo(() => {
    return getBaseThemeOverrideMap(userSettings);
  }, [userSettings]);

  const baseThemeOverride = useMemo(() => {
    if (!activeBaseKey) return null;
    return normalizeThemeMode(themeModeByBaseOverride?.[activeBaseKey]);
  }, [activeBaseKey, themeModeByBaseOverride]);

  const baseDefaultThemeModeRaw = useMemo(() => {
    return normalizeThemeMode(activeBase?.[BASE_DEFAULT_THEME_FIELD]);
  }, [activeBase]);

  const baseDefaultThemeMode = useMemo(() => {
    return baseDefaultThemeModeRaw || ThemeMode.DARK;
  }, [baseDefaultThemeModeRaw]);

  const legacyBaseThemeMode = useMemo(() => {
    if (!activeBaseKey) return null;
    if (baseThemeOverride) return null;
    if (baseDefaultThemeModeRaw) return null;

    const prefs = readStoredPreferences();
    return normalizeThemeMode(prefs?.[activeBaseKey]);
  }, [activeBaseKey, baseDefaultThemeModeRaw, baseThemeOverride]);

  const themeMode = baseThemeOverride
    || baseDefaultThemeModeRaw
    || legacyBaseThemeMode
    || ThemeMode.DARK;

  const theme = useMemo(
    () => resolveTheme(activeThemePack, themeMode),
    [activeThemePack, themeMode],
  );

  const isUsingBaseThemeDefault = !baseThemeOverride;

  const themePreferenceSource = baseThemeOverride
    ? 'override'
    : (baseDefaultThemeModeRaw ? 'base-default' : (legacyBaseThemeMode ? 'legacy-local' : 'fallback'));

  const persistUserThemeSettings = useCallback(async (nextOverrideMap) => {
    if (!userKey) return;

    const patch = buildThemeOverrideSettingPatch(nextOverrideMap);

    try {
      if (userSettings?.id || userSettings?.userKey) {
        await updateSetting(userKey, patch);
        return;
      }

      await createSetting(userKey, patch);
    } catch (error) {
      if (error?.code === 'not-found') {
        await createSetting(userKey, patch);
        return;
      }
      throw error;
    }
  }, [userKey, userSettings?.id, userSettings?.userKey]);

  const setThemeModeOverride = useCallback(async (nextMode) => {
    if (!activeBaseKey || !userKey) return;

    const normalizedMode = normalizeThemeMode(nextMode) || ThemeMode.DARK;
    const nextOverrideMap = {
      ...getBaseThemeOverrideMap(userSettings),
      [activeBaseKey]: normalizedMode,
    };

    if (typeof setUserSettings === 'function') {
      setUserSettings((previous) => ({
        ...(previous || {}),
        userKey,
        ...buildThemeOverrideSettingPatch({
          ...getBaseThemeOverrideMap(previous),
          [activeBaseKey]: normalizedMode,
        }),
      }));
    }

    try {
      await persistUserThemeSettings(nextOverrideMap);
    } catch (error) {
      console.error('ThemeContext: failed to save theme override', error);
    }
  }, [activeBaseKey, persistUserThemeSettings, setUserSettings, userKey, userSettings]);

  const clearThemeModeOverride = useCallback(async () => {
    if (!activeBaseKey || !userKey) return;

    const nextOverrideMap = {
      ...getBaseThemeOverrideMap(userSettings),
    };
    delete nextOverrideMap[activeBaseKey];

    if (typeof setUserSettings === 'function') {
      setUserSettings((previous) => {
        const previousMap = {
          ...getBaseThemeOverrideMap(previous),
        };
        delete previousMap[activeBaseKey];

        return {
          ...(previous || {}),
          userKey,
          ...buildThemeOverrideSettingPatch(previousMap),
        };
      });
    }

    try {
      await persistUserThemeSettings(nextOverrideMap);
    } catch (error) {
      console.error('ThemeContext: failed to clear theme override', error);
    }
  }, [activeBaseKey, persistUserThemeSettings, setUserSettings, userKey, userSettings]);

  const setBaseDefaultThemeMode = useCallback(async (nextMode) => {
    if (!activeBaseKey) return;

    const normalizedMode = normalizeThemeMode(nextMode) || ThemeMode.DARK;

    if (typeof setActiveBase === 'function') {
      setActiveBase((previous) => {
        if (!previous) return previous;

        const previousKey = typeof previous?.key === 'string'
          ? previous.key.trim()
          : '';

        if (previousKey !== activeBaseKey) return previous;

        return {
          ...previous,
          [BASE_DEFAULT_THEME_FIELD]: normalizedMode,
        };
      });
    }

    try {
      await updateBase(activeBaseKey, {
        [BASE_DEFAULT_THEME_FIELD]: normalizedMode,
      });
    } catch (error) {
      console.error('ThemeContext: failed to update base default theme mode', error);
    }
  }, [activeBaseKey, setActiveBase]);

  useEffect(() => {
    if (!activeBaseKey || !userKey) return;
    if (baseThemeOverride) return;
    if (baseDefaultThemeModeRaw) return;

    const legacyMode = legacyBaseThemeMode;
    if (!legacyMode) return;

    const migrationKey = `${userKey}:${activeBaseKey}`;
    if (migratedLegacyBaseRef.current.has(migrationKey)) return;
    migratedLegacyBaseRef.current.add(migrationKey);

    const nextOverrideMap = {
      ...getBaseThemeOverrideMap(userSettings),
      [activeBaseKey]: legacyMode,
    };

    if (typeof setUserSettings === 'function') {
      setUserSettings((previous) => ({
        ...(previous || {}),
        userKey,
        ...buildThemeOverrideSettingPatch({
          ...getBaseThemeOverrideMap(previous),
          [activeBaseKey]: legacyMode,
        }),
      }));
    }

    (async () => {
      try {
        await persistUserThemeSettings(nextOverrideMap);
      } catch (error) {
        console.error('ThemeContext: failed to migrate legacy base theme preference', error);
      }
    })();
  }, [
    activeBaseKey,
    legacyBaseThemeMode,
    persistUserThemeSettings,
    setUserSettings,
    userKey,
    userSettings,
    baseDefaultThemeModeRaw,
    baseThemeOverride,
  ]);

  const value = useMemo(() => ({
    themeMode,
    theme,
    themePackId: activeThemePack?.id || ORBITAL_DEFAULT_PACK.id,
    setThemeMode: setThemeModeOverride,
    setThemeModeOverride,
    clearThemeModeOverride,
    setBaseDefaultThemeMode,
    isUsingBaseThemeDefault,
    baseDefaultThemeMode,
    themePreferenceSource,
    themes,
  }), [
    activeThemePack?.id,
    clearThemeModeOverride,
    isUsingBaseThemeDefault,
    setThemeModeOverride,
    setBaseDefaultThemeMode,
    theme,
    themeMode,
    themePreferenceSource,
    themes,
    baseDefaultThemeMode,
  ]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
